/**
 * Model View Toggle Action
 * Triggered by: arc:action:modelViewToggle
 */

import { readStoredJson } from "@js/utils/storage";

/**
 * The saved model-view state, or false.
 *
 * This runs when the module is imported, before Alpine starts, so it must never throw:
 * with site data blocked, reading localStorage throws a SecurityError, and an uncaught
 * error at import time stops the whole theme bundle — Alpine never boots and every
 * component on the page, the vehicle finder included, is left dead.
 *
 * @returns {boolean}
 */
function readSavedModelView() {
    return readStoredJson('modelview_state', false) === true;
}

// Global model view state persisted in localStorage
let isModelView = readSavedModelView();

// Debounce utility function
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

export const runModelViewToggle = async (data = {}) => {
    try {
        const { 
            isModelViewValue, 
            toggle = true,
            dispatchLegacyEvents = true 
        } = data;

        // If specific value is provided, use it; otherwise toggle current state
        if (typeof isModelViewValue !== 'undefined') {
            isModelView = isModelViewValue;
        } else if (toggle) {
            isModelView = !isModelView;
        }

        // Save to localStorage for persistence
        localStorage.setItem('modelview_state', JSON.stringify(isModelView));

        // Dispatch legacy events for backwards compatibility
        if (dispatchLegacyEvents) {
            window.dispatchEvent(new CustomEvent('modelview-toggle', {
                detail: { isModelView }
            }));

            window.dispatchEvent(new CustomEvent('modelview-toggled', {
                detail: { isModelView }
            }));
        }

        // Dispatch new event for the actions system
        window.dispatchEvent(new CustomEvent('modelView:updated', {
            detail: {
                isModelView,
                timestamp: new Date().toISOString()
            }
        }));

        return {
            success: true,
            isModelView,
            previousState: !isModelView
        };

    } catch (error) {
        console.error('Error toggling model view:', error);
        
        // Dispatch error event
        window.dispatchEvent(new CustomEvent('modelView:error', {
            detail: {
                error: error.message,
                timestamp: new Date().toISOString()
            }
        }));

        return {
            success: false,
            error: error.message,
            isModelView
        };
    }
};

// Debounced version for high-frequency toggling
export const runModelViewToggleDebounced = debounce(runModelViewToggle, 300);

// Helper function to get current model view state
export const getModelViewState = () => readSavedModelView();

// Helper function to set model view state without toggling
export const setModelViewState = async (isModelViewValue) => {
    return await runModelViewToggle({ 
        isModelViewValue, 
        toggle: false 
    });
};

// Initialize model view state and set up event listeners
export const initializeModelView = () => {
    try {
        // Dispatch initial state
        window.dispatchEvent(new CustomEvent('modelView:initialized', {
            detail: {
                isModelView: getModelViewState(),
                timestamp: new Date().toISOString()
            }
        }));

        // Listen for legacy modelview-toggle events for backwards compatibility
        window.addEventListener('modelview-toggle', (event) => {
            runModelViewToggle({
                isModelViewValue: event.detail.isModelView,
                toggle: false,
                dispatchLegacyEvents: false // Prevent infinite loop
            });
        });

        return {
            success: true,
            isModelView: getModelViewState()
        };

    } catch (error) {
        console.error('Error initializing model view:', error);
        return {
            success: false,
            error: error.message
        };
    }
};
